import { twMerge } from "tailwind-merge";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Form,
  H4,
  H6,
  InputTextArea,
} from "@sellar-io/components";
import { SupplierGetResponseType } from "@sellar-io/api-v2/types";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Icon } from "../../../components";
import { InputLabel, InputContainer } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { useSupplierUpdate } from "../../../hooks/api/v2/supplier/useSupplierUpdate";
import { useSellarSupplier } from "../../../hooks/useSellarSupplier";

export const StorefrontSettingsDeliveryNotesCard = ({
  deliveryNotes,
}: {
  deliveryNotes: SupplierGetResponseType["deliveryNotes"];
}) => {
  const supplier = useSellarSupplier();

  const [modalActive, setModalActive] = useState(false);

  const supplierUpdateMutation = useSupplierUpdate(supplier.id);

  return (
    <Card className="bg-white">
      <CardHeader
        className={twMerge(
          "bg-white",
          "flex items-center justify-between gap-6 p-4",
        )}
      >
        <div>
          <H4>Delivery notes</H4>
          <H6>
            Provide customers with as much details about your delivery schedule,
            charges, and more.
          </H6>
        </div>
        <Button color={"secondary"} onClick={() => setModalActive(true)}>
          <Icon icon={"PENCIL"} />
          Edit
        </Button>
      </CardHeader>
      <CardContent className={"p-4"}>
        <pre className={"whitespace-pre-wrap"}>
          {deliveryNotes ?? "No delivery notes"}
        </pre>
      </CardContent>
      <StorefrontSettingsDeliveryNotesModal
        deliveryNotes={deliveryNotes}
        loading={supplierUpdateMutation.isPending}
        active={modalActive}
        onDismiss={() => setModalActive(false)}
        onSubmit={(deliveryNotes) => {
          supplierUpdateMutation.mutate(
            {
              deliveryNotes,
            },
            {
              onSuccess: () => {
                setModalActive(false);
              },
            },
          );
        }}
      />
    </Card>
  );
};

const StorefrontSettingsDeliveryNotesModal = ({
  deliveryNotes,
  loading,
  active,
  onDismiss,
  onSubmit,
}: {
  deliveryNotes: SupplierGetResponseType["deliveryNotes"];
  loading: boolean;
  active: boolean;
  onDismiss: () => void;
  onSubmit: (deliveryNotes: string | null) => void;
}) => {
  const { handleSubmit, register, formState, reset } = useForm({
    shouldUseNativeValidation: true,
    defaultValues: {
      deliveryNotes,
    },
  });

  useEffect(() => {
    reset({ deliveryNotes });
  }, [reset, deliveryNotes]);

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        title={"Delivery notes"}
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
      />
      <Form
        className="flex w-full flex-col"
        onSubmit={handleSubmit((data) => {
          onSubmit(data.deliveryNotes || null);
        })}
      >
        <DialogContent>
          <InputLabel>Delivery notes</InputLabel>
          <InputContainer>
            <InputTextArea
              variant="legacyFixed"
              {...register("deliveryNotes", {
                required: false,
                disabled: loading,
              })}
              placeholder={"Delivery notes"}
            />
          </InputContainer>
        </DialogContent>

        <DialogCTA>
          <Button
            type={"submit"}
            className={"w-full"}
            loading={loading}
            disabled={!formState.isDirty}
          >
            Update
          </Button>
        </DialogCTA>
      </Form>
    </Dialog>
  );
};
